import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import TextInput from "./FormFields/TextInput";
import SelectOption from "./FormFields/SelectOption";
import CheckboxField from "./FormFields/CheckboxField";

import FileInput from "./FormFields/FileInput";
import world from "../utils/wordlCountries";
import TermsModal from "./modals/TermsModal";
import { Link } from "react-router-dom";

function SignUp({ homeRef, handleScroll }) {
  const validationString = "must not be empty";
  const FILE_SIZE = 2000000;

  const [fileValue, setFileValue] = useState("");
  const [fileError, setFileError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const initialValues = {
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    addressOne: "",
    addressTwo: "",
    city: "",
    postcode: "",
    country: "",
    idType: "",
    idNumber: "",
    expiryDate: "",
    idImage: {},
    password: "",
    passwordConfirm: "",
    acceptTerms: false,
  };

  const schema = Yup.object({
    firstName: Yup.string()
      .max(30, "Must be 30 characters or less")
      .required(`First Name ${validationString}`),
    lastName: Yup.string()
      .max(30, "Must be 30 characters or less")
      .required(`Last Name ${validationString}`),
    email: Yup.string()
      .email("Invalid email address")
      .required("Provide valid email address"),
    phone: Yup.number().positive().integer().required("Only numbers"),
    addressOne: Yup.string().required(`Address ${validationString}`),
    city: Yup.string().required(`City ${validationString}`),
    postcode: Yup.string().required(`Postcode ${validationString}`),
    country: Yup.string().required(`Country ${validationString}`),
    idType: Yup.string().required(`Id type ${validationString}`),
    idNumber: Yup.string().required(`Id number ${validationString}`),
    expiryDate: Yup.date()
      .min(new Date())
      .required(`Expiry date ${validationString}`),
    password: Yup.string()
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.{8,})/,
        "Must Contain 8 Characters, One Uppercase, One Lowercase and One Number"
      )
      .required(`Password ${validationString}`),
    passwordConfirm: Yup.string().oneOf(
      [Yup.ref("password"), null],
      "Passwords must match"
    ),
    acceptTerms: Yup.boolean()
      .required("Required")
      .oneOf([true], "You must accept the terms and conditions."),
  });

  const handleChange = (e) => {
    let file = e.target.files[0];
    setFileValue(file);
    if (file && file.size > FILE_SIZE) {
      setFileError("Image size must not exceed 2MB");
    } else {
      setFileError("");
    }
  };

  const handleSubmit = (values, { setSubmitting }) => {
    if (!fileValue) {
      setFileError("Image must not be empty");
      return;
    }
    let currentValues = { ...values, idImage: fileValue };
    setIsSubmitting(true);
    setTimeout(() => {
      window.location.href = "/admin";
    }, 1000);
  };

  return (
    <>
      <header
        ref={homeRef}
        onScroll={handleScroll}
        className="position-relative w-100 pt-80 height-74 pb-5"
      >
        <div className="position-absolute start-0 top-0 w-100 height-74 linear-gradient-primary-bg z-index-1"></div>
      </header>
      <main className="container">
        <div className="max-width-720 mx-auto p-2 px-sm-3 py-sm-3 shadow-sm mb-5">
          {isSubmitting ? (
            <div
              style={{ minHeight: "500px" }}
              className="d-flex align-items-center justify-content-center"
            >
              <div
                className="spinner-grow"
                style={{ width: "3rem", height: "3rem" }}
                role="status"
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <h1 className="fs-4 mb-5">Create a New Account</h1>
              <Formik
                initialValues={initialValues}
                validationSchema={schema}
                onSubmit={handleSubmit}
              >
                <Form>
                  <div className="row">
                    <TextInput
                      label="First Name"
                      name="firstName"
                      type="text"
                      placeholder="First Name"
                    />
                    <TextInput
                      label="Last Name"
                      name="lastName"
                      type="text"
                      placeholder="Last Name"
                    />
                    <TextInput
                      label="Email"
                      name="email"
                      type="text"
                      placeholder="Email"
                    />
                    <TextInput
                      label="Phone"
                      name="phone"
                      type="number"
                      placeholder="Phone"
                    />
                    <div className="col-12 mb-3 px-3">
                      <div className="height-1 border-bottom"></div>
                    </div>
                    <TextInput
                      label="Address Line 01"
                      name="addressOne"
                      type="text"
                      placeholder="Address Line 01"
                    />
                    <TextInput
                      label="Address Line 02"
                      name="addressTwo"
                      type="text"
                      placeholder="Address Line 01"
                    />
                    <TextInput
                      label="City"
                      name="city"
                      type="text"
                      placeholder="City"
                    />
                    <TextInput
                      label="Post Code"
                      name="postcode"
                      type="text"
                      placeholder="Post Code"
                    />
                    <SelectOption label="Country" name="country">
                      <option value="">Select country</option>
                      {world.map((country) => (
                        <option key={country.name} value={country.name}>
                          {country.name}
                        </option>
                      ))}
                    </SelectOption>
                    <div className="col-12 mb-3 px-3">
                      <div className="height-1 border-bottom"></div>
                    </div>
                    <SelectOption label="Id Type" name="idType">
                      <option value="">Select Id Type</option>
                      <option value="passport">Passport</option>
                      <option value="driving_licence">Driving Licence</option>
                      <option value="id_card_eu">ID Card - Europe</option>
                      <option value="SSN">SSN</option>
                      <option value="other">Other</option>
                    </SelectOption>
                    <TextInput
                      label="Id Number"
                      name="idNumber"
                      type="text"
                      placeholder="Id Number"
                    />
                    <TextInput
                      label="Expiry Date"
                      name="expiryDate"
                      type="date"
                      placeholder="Expiry Date"
                    />
                    <FileInput
                      handleChange={handleChange}
                      errorMessage={fileError}
                    />

                    <div className="col-12 mb-3 px-3">
                      <div className="height-1 border-bottom"></div>
                    </div>
                    <TextInput
                      label="Password"
                      name="password"
                      type="password"
                      placeholder="Password"
                    />
                    <TextInput
                      label="Confirm Password"
                      name="passwordConfirm"
                      type="password"
                      placeholder="Confirm Password"
                    />
                    <CheckboxField name="acceptTerms">
                      I accept the
                    </CheckboxField>

                    <div className="col-12">
                      <div className="d-grid my-3">
                        <button
                          className="btn btn-lg btn-primary boxshadow-none outline-none"
                          type="submit"
                        >
                          Submit
                        </button>
                      </div>
                    </div>
                  </div>
                </Form>
              </Formik>
            </>
          )}
          <div className="col-12">
            <p className="my-5">
              Already have an account?
              <Link className="mx-1 text-dark" to="/login">
                Login
              </Link>
            </p>
          </div>
        </div>
        <TermsModal />
      </main>
    </>
  );
}

export default SignUp;
